// DeviceWarning.jsx (you can paste this into AdminLogin.jsx file above the component or in a new file)
import React, { useEffect, useState } from 'react';

function detectDeviceType() {
  if (typeof window === 'undefined') return 'desktop';

  // Media queries (primary)
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';

  // Fallback to userAgent sniffing for tablet-ish devices
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/ipad|tablet|playbook/i.test(ua) || (/\bAndroid\b/i.test(ua) && !/\bMobile\b/i.test(ua))) {
    return 'tablet';
  }

  return 'desktop';
}

export default function DeviceWarning({
  storageKey = 'device_warning_dismissed',
  showForeverKey = 'device_warning_dismissed_forever'
} = {}) {
  const [device, setDevice] = useState('desktop');
  const [visible, setVisible] = useState(false);
  const [dismissForever, setDismissForever] = useState(
    !!localStorage.getItem(showForeverKey)
  );

  useEffect(() => {
    const update = () => setDevice(detectDeviceType());
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    // only show on mobile/tablet and if not dismissed
    const dismissed = localStorage.getItem(storageKey) === 'true';
    if ((device === 'mobile' || device === 'tablet') && !dismissed && !dismissForever) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [device, dismissForever, storageKey]);

  const dismiss = (forever = false) => {
    localStorage.setItem(storageKey, 'true');
    if (forever) {
      localStorage.setItem(showForeverKey, 'true');
      setDismissForever(true);
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    // Using your warning styles from earlier if present, otherwise simple inline fallback
    <div className="warning-overlay" role="dialog" aria-modal="true" aria-label="Device notice">
      <div className="warning-dialog">
        <div className="warning-header">
          <h3>Recommended on Large Screens</h3>
        </div>
        <div className="warning-content">
          <p>
            This website and its AI interview experience are optimised for larger screens (laptops & desktops).
            On smaller devices (phones and tablets) some UI elements may not behave as expected.
          </p>
          <p style={{ fontWeight: 600 }}>
            We strongly recommend using a laptop or desktop for the best experience.
          </p>
        </div>
        <div className="warning-actions">
          <button
            className="warning-btn cancel-btn"
            onClick={() => dismiss(false)}
            aria-label="Close notice"
            style={{ minWidth: 140 }}
          >
            Continue on this device
          </button>
          <button
            className="warning-btn confirm-btn"
            onClick={() => dismiss(true)}
            aria-label="Don't show again"
            style={{ minWidth: 160 }}
          >
            Don't show again
          </button>
        </div>
      </div>

      {/* small inline style fallback if your CSS isn't present */}
      <style jsx>{`
        .warning-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); display:flex; align-items:center; justify-content:center; z-index:9999; }
        .warning-dialog { width: 92%; max-width: 500px; border-radius: 12px; overflow:hidden; background: #fff; box-shadow: 0 30px 60px rgba(0,0,0,0.35); }
        .warning-header { background:  linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%); color:#fff; padding:1.25rem; text-align:center; }
        .warning-content { padding:1.5rem; color:#374151; text-align:center; }
        .warning-actions { display:flex; gap:12px; justify-content:center; padding:1rem; background:#f9fafb; border-top:1px solid #e5e7eb; }
        .warning-btn { padding:0.6rem 1rem; border-radius:8px; font-weight:600; border:none; cursor:pointer; }
        .cancel-btn { background:#6b7280; color:#fff; }
        .confirm-btn { background:#dc2626; color:#fff; }
        @media (min-width: 1024px) { .warning-overlay { display:none; } }
      `}</style>
    </div>
  );
}
